import type { AppConfigView } from "@/lib/api/types";

/**
 * Value helpers shared by the row, the details drawer and the page's filters,
 * so "what is the value", "is it changed" and "is this draft valid" have one
 * answer each.
 */

/** The value the backend falls back to when no value is stored here. */
export function defaultFor(config: AppConfigView): string {
  // The backend sends the effective default (registry default for registry
  // keys), so an unset key shows its real value, not an empty field.
  if (config.defaultValue) return config.defaultValue;
  if (config.type === "boolean") return "false";
  if (config.type === "integer") return String(config.min ?? 0);
  return "";
}

/** What the backend reads right now: the stored value, else the default. */
export function effectiveValue(config: AppConfigView): string {
  return config.value ?? defaultFor(config);
}

export function isOn(value: string): boolean {
  return value.trim().toLowerCase() === "true";
}

/**
 * Changed from its environment default: a registry key with a value stored
 * here. A custom key has no environment default, so it never counts as
 * "changed" (the board marks it "Custom" instead).
 */
export function isChanged(config: AppConfigView): boolean {
  return config.origin !== "custom" && config.source === "db";
}

/** Accepts only absolute http(s) URLs — mirrors the backend's url-type validation. */
export function isValidUrl(value: string): boolean {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export type DraftProblem = "url" | "integer" | "integerRange" | null;

/** Why a draft can't be saved, or null. An empty draft is left to the backend (a text key may be blank). */
export function draftProblem(config: AppConfigView, draft: string): DraftProblem {
  if (config.type === "url") return isValidUrl(draft) ? null : "url";
  if (config.type === "integer") {
    if (!/^-?\d+$/.test(draft.trim())) return "integer";
    const n = Number(draft);
    if ((config.min != null && n < config.min) || (config.max != null && n > config.max)) return "integerRange";
  }
  return null;
}

export function formatDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

type Translate = (key: string, values?: Record<string, string | number>) => string;

/** A value as an admin reads it: On / Off for a switch, "Not set" for blank. */
export function formatValue(config: AppConfigView, value: string | null, t: Translate): string {
  if (config.type === "boolean") return value != null && isOn(value) ? t("appConfigs.valueOn") : t("appConfigs.valueOff");
  return value == null || value === "" ? t("appConfigs.valueNotSet") : value;
}
